import { useId } from "react";
import { CodeMarkdown } from "@/components/storyboard/code/CodeMarkdown";
import { CodeQuizWorkspace } from "@/components/storyboard/code/CodeQuizWorkspace";
import InputPrivacyNotice from "@/components/storyboard/InputPrivacyNotice";
import { CodeQuizActions } from "@/components/storyboard/quiz/CodeQuizActions";
import { codeQuizReview } from "@/components/storyboard/quiz/CodeQuizVerdict";
import { QuizChallengeHeading } from "@/components/storyboard/quiz/QuizChallengeHeading";
import type { ValidationResult } from "@/services/QuizValidationService";
import type { ValidationSubmission } from "@/lib/validationSubmission";
import { useQuizCodeState } from "@/hooks/useQuizCodeState";
import type { QuizCodeExplanationEntryData } from "@/types/storyboard";
import {
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

export interface QuizCodeExplanationProps extends HiddenVerdictProps {
  entries: QuizCodeExplanationEntryData[];
  /** Submits the learner's explanation for server-side (LLM) validation; also persists it. */
  onValidate: (
    explanation: string,
    entryIndex: number
  ) => Promise<ValidationResult>;
  /** Saved submissions per entry index, used to re-seed the textarea on reopen. */
  initialSubmissions?: Record<number, ValidationSubmission>;
  /** Called after a submission is persisted, so saved state can be refreshed. */
  onSubmitted?: () => void;
}

/**
 * Code-explanation quiz: the learner writes, in their own words, what a code/text
 * snippet means and does. "Erklärung prüfen" runs server-side (LLM) validation,
 * which also persists the answer. Each task grants a limited number of tries;
 * no localStorage — durable state lives in validation_data.
 */
const QuizCodeExplanation = ({
  entries,
  onValidate,
  initialSubmissions,
  onSubmitted,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizCodeExplanationProps) => {
  const privacyNoticeId = useId();
  const quiz = useQuizCodeState({
    entries,
    initialSubmissions,
    onValidate,
    onSubmitted,
    revealFeedback,
    onAdvance,
    getDefaultInput: () => "",
  });
  const {
    currentEntry,
    currentInput: currentText,
    currentResult,
    isSettled,
    isValidating,
    handleInputChange: handleTextChange,
  } = quiz;

  if (!entries || entries.length === 0) return null;
  if (!currentEntry) return null;

  const meta = (
    <>
      <QuizChallengeHeading challenge={currentEntry.challenge} />

      <CodeMarkdown>{currentEntry.codeSnippet}</CodeMarkdown>
    </>
  );

  const editor = (
    <div className="flex h-full flex-col gap-2">
      <InputPrivacyNotice id={privacyNoticeId} className="shrink-0" />
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-gray-700">
        <div className="shrink-0 px-4 py-2">
          <span className="font-semibold text-gray-300 uppercase tracking-wide">
            Deine Erklärung
          </span>
        </div>
        <textarea
          className="min-h-0 flex-1 resize-none bg-transparent px-4 pb-3 leading-relaxed text-gray-100 outline-none placeholder:text-gray-400"
          value={currentText}
          onChange={(event) => handleTextChange(event.target.value)}
          readOnly={isValidating || isSettled}
          placeholder="Erkläre in eigenen Worten, was der Code bzw. Text bedeutet, wofür er gedacht ist und welche Konsequenzen sich daraus ergeben …"
          aria-label="Deine Erklärung"
          aria-describedby={privacyNoticeId}
        />
      </div>
    </div>
  );

  const actions = (
    <CodeQuizActions
      quiz={quiz}
      submitLabel={
        collectsWithoutVerdict(revealFeedback)
          ? (advanceLabel ?? "Nächste Frage")
          : "Erklärung prüfen"
      }
      canSubmit={currentText.trim().length > 0}
      navVariant="secondary"
    />
  );

  return (
    <CodeQuizWorkspace
      meta={meta}
      feedback={codeQuizReview(
        currentResult,
        "Nutze das Feedback und überarbeite deine Erklärung – sie bleibt erhalten."
      )}
      editor={editor}
      actions={actions}
      split="even"
    />
  );
};

export default QuizCodeExplanation;
